import { useEffect, useRef, useState, type ReactNode } from "react";
import { Plus, X } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CadastroDraft } from "@/lib/cadastro-draft";

export function CadastroMultiField({
	name,
	label,
	labelAction,
	slot,
	addLabel,
	placeholder,
	type,
	mask,
}: {
	name: "phones" | "emails";
	label: string;
	labelAction?: ReactNode;
	slot: string;
	addLabel: string;
	placeholder?: string;
	type?: string;
	mask?: (value: string) => string;
}) {
	const { control } = useFormContext<CadastroDraft>();
	const controlled = useController({ control, name });
	const hasEmptyValue = controlled.field.value.some((value) => value.trim() === "");
	const nextId = useRef(0);
	const mint = () => {
		const id = nextId.current;
		nextId.current += 1;

		return id;
	};
	const [ids, setIds] = useState(() => controlled.field.value.map(mint));

	useEffect(() => {
		setIds((current) => {
			if (current.length === controlled.field.value.length) {
				return current;
			}

			return controlled.field.value.map((_, index) => current[index] ?? mint());
		});
	}, [controlled.field.value]);

	const update = (index: number, value: string) => {
		controlled.field.onChange(
			controlled.field.value.map((current, itemIndex) => {
				if (itemIndex !== index) {
					return current;
				}

				return mask ? mask(value) : value;
			}),
		);
	};

	const add = () => {
		if (hasEmptyValue) {
			return;
		}

		setIds((current) => [...current, mint()]);
		controlled.field.onChange([...controlled.field.value, ""]);
	};

	const remove = (index: number) => {
		const values = controlled.field.value.filter((_, itemIndex) => itemIndex !== index);

		if (values.length === 0) {
			setIds([mint()]);
			controlled.field.onChange([""]);

			return;
		}

		setIds((current) => current.filter((_, itemIndex) => itemIndex !== index));
		controlled.field.onChange(values);
	};

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-1.5">
				<Label>{label}</Label>
				{labelAction}
			</div>

			{controlled.field.value.map((value, index) => (
				<div key={ids[index]} className="flex items-center gap-2">
					<Input
						type={type}
						value={value}
						placeholder={placeholder}
						data-slot={`${slot}-${index}`}
						onChange={(event) => update(index, event.target.value)}
					/>

					<Button
						type="button"
						variant="ghost"
						size="icon"
						data-slot={`${slot}-remove-${index}`}
						onClick={() => remove(index)}
					>
						<X />
					</Button>
				</div>
			))}

			<Button
				type="button"
				variant="outline"
				size="sm"
				className="self-start"
				data-slot={`${slot}-add`}
				disabled={hasEmptyValue}
				onClick={add}
			>
				<Plus />
				{addLabel}
			</Button>
		</div>
	);
}
